/*eslint-disable max-lines*/
import { useTwilioToken } from '@hakimo-ui/hakimo/data-access';

import {
  CallEndIcon,
  CallingIcon,
  DialpadIcon,
  MicOff,
  MicOn,
} from '@hakimo-ui/hakimo/ui-elements';
import { toast } from '@hakimo-ui/hakimo/util';
import { Button, HakimoSpinner } from '@hakimo-ui/shared/ui-base';
import { Call, Device } from '@twilio/voice-sdk';
import { useEffect, useRef, useState } from 'react';
import DraggableHOC from '../draggable-hoc/DraggableHOC';
import Keypad from './Keypad';

interface Props {
  toName: string;
  toNumber: string;
  onCloseCb: () => void;
  locationTenantId?: string;
  locationId?: number;
  onCallInitiated?: (call: Call) => void;
  onCallDisconnected?: (call: Call) => void;
  onErrorCb?: (err: Error) => void;
}

export function TwilioCall(props: Props) {
  const {
    toName,
    toNumber,
    onCloseCb,
    locationTenantId,
    locationId,
    onCallInitiated,
    onCallDisconnected,
    onErrorCb,
  } = props;
  const [showKeypad, setShowKeypad] = useState(false);
  const [device, setDevice] = useState<Device>();
  const [call, setCall] = useState<Call>();
  const [twilioToken, setTwilioToken] = useState('');
  const [callInProgress, setCallInProgress] = useState(false);
  const [deviceConnected, setDeviceConnected] = useState(false);
  const acceptedRef = useRef(false);
  const erroredRef = useRef(false);
  // in a ref so the device effect below can depend on `device` alone
  const onErrorCbRef = useRef(onErrorCb);
  onErrorCbRef.current = onErrorCb;

  const onSuccess = (token: string) => setTwilioToken(token);
  const onError = (err: Error) => {
    toast("Couldn't connect call - " + err.message, { type: 'error' });
    onErrorCb && onErrorCb(err);
    onCloseCb();
  };
  useTwilioToken(locationTenantId, locationId, onSuccess, onError, toNumber);

  useEffect(() => {
    if (!twilioToken) return;
    if (!device) {
      import('@twilio/voice-sdk').then((twilio) => {
        setDevice(
          new twilio.Device(twilioToken, {
            codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
          })
        );
      });
    } else {
      device.updateToken(twilioToken);
    }
  }, [device, setDevice, twilioToken]);

  // Attach the error handler once, and destroy the device on unmount. Without
  // the teardown the device keeps its signaling socket open after the call view
  // closes, and Twilio pushes AccessTokenExpired (20104) to that orphan when the
  // token expires an hour later — toasted with no dialer on screen.
  useEffect(() => {
    if (!device) return;
    device.on('error', (err, erroredCall) => {
      erroredRef.current = true;
      toast(err?.message ?? 'Call failed', { type: 'error' });
      onErrorCbRef.current?.(err);
      erroredCall?.disconnect();
    });
    return () => device.destroy();
  }, [device]);

  useEffect(() => {
    if (!device || deviceConnected) return;
    if (!deviceConnected) {
      setDeviceConnected(true);
    }
    call?.disconnect();
    const connectOptions = { To: toNumber };
    setDeviceConnected(true);
    device?.connect({ params: connectOptions }).then((c) => {
      setCall(c);

      c?.on('accept', () => {
        acceptedRef.current = true;
        setCallInProgress(true);
        onCallInitiated && onCallInitiated(c);
      });
      c?.on('error', (err) => {
        erroredRef.current = true;
        toast(err?.message ?? 'Call failed', { type: 'error' });
        onErrorCb && onErrorCb(err);
      });
      c?.on('disconnect', () => {
        setCallInProgress(false);
        if (!erroredRef.current) {
          if (acceptedRef.current) {
            toast('Call ended');
          } else {
            toast('Call could not connect', { type: 'error' });
          }
        }
        onCallDisconnected && onCallDisconnected(c);
        onCloseCb();
      });
    });
  }, [
    call,
    device,
    deviceConnected,
    onCallDisconnected,
    onCallInitiated,
    onCloseCb,
    onErrorCb,
    toName,
    toNumber,
  ]);

  const closeAndHangup = () => {
    call?.disconnect();
    onCloseCb();
  };

  const onClickKeypad = () => setShowKeypad((prev) => !prev);

  const [isMuted, setIsMuted] = useState(false);

  const toggleMute = () => {
    if (!call) return;
    call.mute(!isMuted);
    setIsMuted(!isMuted);
  };

  return (
    <DraggableHOC>
      <div className="bg-onlight-bg-2 dark:bg-ondark-bg-2 dark:divide-ondark-line-2 space-y-4 divide-y px-6 py-4">
        <div className="flex items-center gap-4">
          <CallingIcon className="dark:fill-ondark-text-1 h-8 w-8" />
          <div className="flex flex-col text-base">
            <span>{toName}</span>
            <span className="text-xs">{toNumber}</span>
          </div>
        </div>
        {callInProgress ? (
          <div className="flex justify-evenly pt-4">
            <Button
              variant="icon"
              onClick={toggleMute}
              title={isMuted ? 'Unmute Mic' : 'Mute Mic'}
            >
              {isMuted ? (
                <MicOff className="dark:text-ondark-text-1 text-onlight-text-3 h-8 w-8" />
              ) : (
                <MicOn className="dark:text-ondark-text-1 text-onlight-text-3 h-8 w-8" />
              )}
            </Button>
            <Button
              variant="icon"
              onClick={onClickKeypad}
              title={`${showKeypad ? 'Hide' : 'Show'} Keypad`}
            >
              <DialpadIcon className="dark:fill-ondark-text-1 h-8 w-8" />
            </Button>
            <Button variant="icon" title="End call" onClick={closeAndHangup}>
              <CallEndIcon className="h-10 w-10 fill-red-500" />
            </Button>
          </div>
        ) : (
          <div className="flex h-24 flex-col items-center justify-center gap-2">
            <HakimoSpinner />
            <span className="animate-pulse">Calling...</span>
          </div>
        )}

        {showKeypad && (
          <div>
            <Keypad onKeypadButtonClick={(digit) => call?.sendDigits(digit)} />
          </div>
        )}
      </div>
    </DraggableHOC>
  );
}

export default TwilioCall;
